import { useEffect, useRef } from 'react';
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { subscribeAnnotations } from '@/lib/realtime/channels';

// Maintains a live Realtime channel for the given document version.
//
// Lifecycle is tied to the Ionic view (not component mount/unmount) because
// Ionic keeps pages mounted in the router stack — useEffect cleanup does not
// fire on navigation away, which would leak channels.
//
// The useEffect on [versionId] handles the race where the published version
// query resolves after the view has already entered.
export function useRealtimeAnnotations(versionId: string | undefined): void {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isActiveRef = useRef(false);

  function open(vid: string) {
    if (channelRef.current) return; // already subscribed for this version
    channelRef.current = subscribeAnnotations(vid, queryClient);
  }

  function close() {
    channelRef.current?.unsubscribe();
    channelRef.current = null;
  }

  useIonViewWillEnter(() => {
    isActiveRef.current = true;
    if (versionId) open(versionId);
  }, [versionId]);

  useIonViewWillLeave(() => {
    isActiveRef.current = false;
    close();
  });

  // Subscribe when versionId arrives after the view has already entered
  // (i.e. the getPublishedVersion query was still loading at view-enter time).
  useEffect(() => {
    if (versionId && isActiveRef.current) open(versionId);
    // Teardown is intentionally handled by useIonViewWillLeave, not here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);
}
